import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mic, Square, Loader2, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function VoiceExpenseDialog({ open, onOpenChange, onCreated }) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [extracted, setExtracted] = useState(null);
  const [error, setError] = useState("");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    if (!open) {
      setRecording(false);
      setProcessing(false);
      setTranscript("");
      setExtracted(null);
      setError("");
    }
  }, [open]);

  const start = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        await upload(blob);
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch {
      setError("Microphone access denied. Allow mic permission and try again.");
    }
  };

  const stop = () => {
    mediaRef.current?.stop();
    setRecording(false);
  };

  const upload = async (blob) => {
    setProcessing(true);
    try {
      const fd = new FormData();
      fd.append("audio", blob, "voice.webm");
      const { data } = await api.post("/ai/voice", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTranscript(data.transcript || "");
      setExtracted(data.extracted || null);
      if (!data.transcript) setError("Couldn't hear anything. Try again.");
    } catch (e) {
      setError(formatApiError(e, "Voice processing failed"));
    } finally {
      setProcessing(false);
    }
  };

  const updateField = (k, v) => setExtracted((p) => ({ ...(p || {}), [k]: v }));

  const save = async () => {
    if (!extracted || !extracted.amount) {
      toast.error("Amount missing");
      return;
    }
    try {
      await api.post("/expenses", {
        amount: Number(extracted.amount),
        category: extracted.category || "Other",
        payment_method: extracted.payment_method || "Cash",
        date: extracted.date || new Date().toISOString().slice(0, 10),
        notes: extracted.notes || transcript,
      });
      toast.success("Voice expense saved");
      onCreated?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="voice-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Voice expense</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-accent/30 p-6">
            <button
              type="button"
              onClick={recording ? stop : start}
              disabled={processing}
              data-testid="voice-record-button"
              className={`flex h-20 w-20 items-center justify-center rounded-full transition-[filter,box-shadow] duration-200 ${
                recording ? "bg-[#C05C4F] text-white animate-pulse" : "bg-primary text-primary-foreground hover:brightness-110"
              }`}
            >
              {processing ? <Loader2 className="h-7 w-7 animate-spin" /> : recording ? <Square className="h-7 w-7" /> : <Mic className="h-7 w-7" />}
            </button>
            <div className="text-sm text-muted-foreground">
              {processing ? "Transcribing & extracting…" : recording ? "Listening… tap to stop" : "Tap to record. Try: \"23 dollars on coffee yesterday\""}
            </div>
          </div>

          {error && <div className="text-sm text-[#C05C4F]">{error}</div>}

          {transcript && (
            <div className="rounded-lg border border-border bg-card p-3 text-sm">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Heard</div>
              <div className="mt-1">{transcript}</div>
            </div>
          )}

          {extracted && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={extracted.amount || ""}
                  onChange={(e) => updateField("amount", e.target.value)}
                  data-testid="voice-amount-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Input value={extracted.category || ""} onChange={(e) => updateField("category", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input type="date" value={extracted.date || new Date().toISOString().slice(0, 10)} onChange={(e) => updateField("date", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Payment</Label>
                <Input value={extracted.payment_method || ""} onChange={(e) => updateField("payment_method", e.target.value)} />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            type="button"
            onClick={save}
            disabled={!extracted}
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="voice-save-button"
          >
            <CheckCircle2 className="h-4 w-4" /> Save expense
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
